/**
 * Shared fetch helpers used by client hooks.
 */

import { getAccessToken, InsufficientCreditsError } from './api-fetch';
export { InsufficientCreditsError };

/**
 * Read the user's AI provider preference from persisted settings.
 * Returns a header record (empty if preference is 'auto' or not set).
 */
function getAiPreferenceHeader(): Record<string, string> {
  try {
    const raw = localStorage.getItem('atsmycv_settings_v1');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed?.aiPreference && parsed.aiPreference !== 'auto') {
      return { 'X-User-AI-Pref': parsed.aiPreference as string };
    }
  } catch {
    /* ignore */
  }
  return {};
}

/** Client-side ceiling for AI requests.
 *
 *  The server runs MiniMax-M2.7 (primary, up to 60 s — a thinking model
 *  that benchmarks at 43 tok/s with a 3.2 s time-to-first-token) and
 *  OpenRouter as fallback (up to 20 s). Worst case sequential path is
 *  ~80 s. We give the client a 90 s ceiling — enough to let both
 *  providers finish, but still bounded so a stuck request aborts
 *  cleanly rather than waiting for Caddy's default read timeout.
 *
 *  Override via `AI_CLIENT_TIMEOUT_MS` at build time if your model
 *  config demands different budgets. */
export const AI_CLIENT_TIMEOUT_MS = 90_000;

/**
 * Parse a Response as JSON, throwing a useful Error if the server returned
 * HTML (e.g. a 504 timeout page) or another non-JSON payload.
 */
export async function parseJsonResponse<T = unknown>(res: Response): Promise<T> {
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    if (/^\s*</.test(text) || text.includes('<!DOCTYPE') || text.toLowerCase().includes('<html')) {
      const extract = (re: RegExp) => (text.match(re) ?? [])[0] ?? '';
      const code = extract(/FUNCTION_INVOCATION_TIMEOUT|FUNCTION_INVOCATION_FAILED|504|502/);
      const msg = code
        ? `Le service IA est saturé (${res.status}). Réessayez dans quelques instants — si le problème persiste, OpenRouter et MiniMax sont tous deux indisponibles.`
        : `Le serveur a renvoyé une page au lieu de JSON (${res.status}). Vérifiez les routes API et les variables d'environnement.`;
      throw new Error(msg);
    }
    throw new Error(text.slice(0, 500) || 'Réponse invalide');
  }
}

/**
 * POST a JSON body to an endpoint with a hard client-side timeout and
 * user-friendly error messages. Used by the AI hooks to avoid hanging
 * forever when the reverse proxy eventually returns 504.
 */
export async function postJsonWithTimeout<T = unknown>(
  url: string,
  body: unknown,
  {
    timeoutMs = AI_CLIENT_TIMEOUT_MS,
    signal,
  }: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<{ res: Response; json: T }> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  // Combine caller's signal with our timeout signal.
  if (signal) {
    if (signal.aborted) ac.abort();
    else signal.addEventListener('abort', () => ac.abort(), { once: true });
  }
  try {
    // Récupère le JWT de la session courante pour l'injecter automatiquement.
    const token = await getAccessToken();
    const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAiPreferenceHeader(), ...authHeader },
      body: JSON.stringify(body),
      signal: ac.signal,
    });

    // Gérer 402 avant parseJsonResponse
    if (res.status === 402) {
      let errData: { error?: string; remaining?: number; plan?: string } = {};
      try { errData = await res.json(); } catch { /* ignore */ }
      throw new InsufficientCreditsError(
        errData.error ?? 'Quota mensuel épuisé. Passez à un plan supérieur.',
        errData.remaining ?? 0,
        errData.plan ?? 'free',
      );
    }

    const json = await parseJsonResponse<T>(res);
    return { res, json };
  } catch (e) {
    if (e instanceof Error && (e.name === 'AbortError' || /abort/i.test(e.message))) {
      throw new Error(
        `Délai dépassé (${Math.round(timeoutMs / 1000)}s) — le service IA n'a pas répondu à temps. Réessayez dans un instant.`,
        { cause: e },
      );
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}
